import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiUser, FiLogOut, FiMenu, FiChevronUp } from 'react-icons/fi';
import { MessagesSquare, Sparkles, Wallet, Settings } from 'lucide-react';
import { AiOutlineClose } from 'react-icons/ai';
import '../styles/components/sidebar.scss';
import DashboardIcon from '../assets/sidebar-icons/dashboard.svg';
import JobSearchIcon from '../assets/sidebar-icons/job-search.svg';
import BYOJIcon from '../assets/sidebar-icons/byoj.svg';
import ApplicationTrackerIcon from '../assets/sidebar-icons/application-tracker.svg';
import axiosClient from '../axios-client';
import { jobTitles, JobOption } from '../data/jobTitles';
import { useUser } from '../contexts/UserContext';

// Job Matching Dropdown Component
interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  label: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Ensure dropdown properly closes on selection and outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    // Handle ESC key to close dropdown
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  const handleOptionSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  // Style to make the dropdown appear as an overlay without affecting layout
  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    zIndex: 9999,
    maxHeight: '200px',
    overflowY: 'auto',
    backgroundColor: 'white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    borderRadius: '6px',
    marginTop: '4px'
  };

  return (
    <div className="job-matching-dropdown-field" ref={dropdownRef}>
      <label>{label}</label>
      <div className="job-matching-dropdown">
        <button
          type="button"
          className={`dropdown-trigger ${isOpen ? "open" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {value}
        </button>
        {isOpen && (
          <div 
            className="dropdown-menu" 
            role="listbox"
            style={dropdownStyle}
          >
            {options.map((option) => (
              <div
                key={option}
                className={`dropdown-item ${value === option ? "selected" : ""}`}
                onClick={() => handleOptionSelect(option)}
                role="option"
                aria-selected={value === option}
              >
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Add a new component for the selectable options
interface OptionBubblesProps {
  options: string[];
  selectedOption: string;
  onChange: (option: string) => void;
  label: string;
}

const OptionBubbles: React.FC<OptionBubblesProps> = ({
  options,
  selectedOption,
  onChange,
  label
}) => {
  return (
    <div className="option-bubbles-field">
      <label>{label}</label>
      <div className="option-bubbles-container">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={`option-bubble ${selectedOption === option ? 'selected' : ''}`}
            onClick={() => onChange(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

// Smart Predictive Input Component
interface SmartPredictiveInputProps {
  value: string;
  onChange: (value: string) => void;
  jobTitles: JobOption[];
  placeholder?: string;
  label?: string;
}

const SmartPredictiveInput: React.FC<SmartPredictiveInputProps> = ({
  value,
  onChange,
  jobTitles,
  placeholder = "Enter job title",
  label
}) => {
  const [suggestedText, setSuggestedText] = useState("");
  const [matchingJob, setMatchingJob] = useState<JobOption | null>(null);
  const [didYouMean, setDidYouMean] = useState<JobOption | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Advanced job title matching with seniority extraction and core role matching
  const findBestMatch = (input: string): { suggestion: string, job: JobOption | null, didYouMean: JobOption | null } => {
    if (!input.trim()) {
      return { suggestion: "", job: null, didYouMean: null };
    }
    
    const inputLower = input.toLowerCase();
    
    // Check for exact matches first
    const exactMatch = jobTitles.find(job => 
      job.label.toLowerCase() === inputLower
    );
    
    if (exactMatch) {
      return { suggestion: "", job: exactMatch, didYouMean: null };
    }

    // Seniority level extraction patterns
    const seniorityPatterns = [
      { pattern: /^(senior|sr\.?|lead)\s+(.+)$/i, level: "Senior" },
      { pattern: /^(junior|jr\.?)\s+(.+)$/i, level: "Junior" },
      { pattern: /^(principal|staff)\s+(.+)$/i, level: "Principal" },
      { pattern: /^(associate|asst\.?)\s+(.+)$/i, level: "Associate" },
      { pattern: /^(mid|intermediate|mid-level)\s+(.+)$/i, level: "Mid-level" },
      { pattern: /^(chief|head|director\s+of)\s+(.+)$/i, level: "Executive" },
      { pattern: /(.+)\s+(intern|internship)$/i, level: "Intern", reverse: true },
    ];
    
    // Extract seniority and core role if present
    let coreRole = inputLower;
    let seniorityLevel = "";
    
    for (const { pattern, level, reverse } of seniorityPatterns) {
      const match = inputLower.match(pattern);
      if (match) {
        seniorityLevel = level;
        coreRole = reverse ? match[1] : match[2];
        break;
      }
    }

    // Industry-standard abbreviations and common terms
    const commonTerms = {
      "pm": ["Product Manager", "Project Manager", "Program Manager"],
      "swe": ["Software Engineer", "Software Developer"],
      "dev": ["Developer", "Development"],
      "eng": ["Engineer", "Engineering"],
      "ui/ux": ["UI/UX Designer", "User Interface Designer", "User Experience Designer"],
      "ux": ["UX Designer", "User Experience Designer"],
      "ui": ["UI Designer", "User Interface Designer"],
      "fp&a": ["Financial Planning and Analysis", "Financial Analyst"],
      "fpa": ["Financial Planning and Analysis", "Financial Analyst"],
      "ml": ["Machine Learning Engineer", "Machine Learning"],
      "ai": ["AI Engineer", "Artificial Intelligence"],
      "devops": ["DevOps Engineer", "Development Operations"],
      "qa": ["Quality Assurance Engineer", "QA Analyst"],
      "hr": ["Human Resources", "HR Specialist"],
      "bd": ["Business Development", "Business Developer"],
      "cs": ["Customer Success", "Customer Support"]
    };

    // Special handling for titles with special characters or multiple word forms
    const specialTitleMappings: {[key: string]: string[]} = {
      "ui ux": ["UI/UX Designer", "UX/UI Designer"],
      "ui and ux": ["UI/UX Designer", "UX/UI Designer"],
      "ux ui": ["UX/UI Designer", "UI/UX Designer"],
      "ux and ui": ["UX/UI Designer", "UI/UX Designer"],
      "financial planning and analysis": ["FP&A Analyst", "Financial Planning & Analysis"],
      "financial planning analyst": ["FP&A Analyst", "Financial Planning & Analysis Analyst"],
      "customer success": ["Customer Success Manager", "Customer Success Representative"],
      "frontend": ["Frontend Developer", "Frontend Engineer"],
      "front end": ["Frontend Developer", "Frontend Engineer"],
      "backend": ["Backend Developer", "Backend Engineer"],
      "back end": ["Backend Developer", "Backend Engineer"],
      "fullstack": ["Full Stack Developer", "Full Stack Engineer"],
      "full stack": ["Full Stack Developer", "Full Stack Engineer"]
    };

    // Normalize input to handle special cases
    const normalizedInput = inputLower
      .replace(/[\/\-&]/g, ' ')  // Replace slashes, dashes, and ampersands with spaces
      .replace(/\s+/g, ' ')      // Normalize spaces
      .trim();
      
    // Check for special title mappings
    for (const [specialForm, mappedTitles] of Object.entries(specialTitleMappings)) {
      if (normalizedInput === specialForm || normalizedInput.includes(specialForm)) {
        const specialMatches = mappedTitles.flatMap(title => 
          jobTitles.filter(job => 
            job.label.toLowerCase().includes(title.toLowerCase())
          )
        );
        
        if (specialMatches.length > 0) {
          return {
            suggestion: "",
            job: null,
            didYouMean: specialMatches[0]
          };
        }
      }
    }
      
    // Check for abbreviation matches first - with improved handling
    for (const [abbr, expansions] of Object.entries(commonTerms)) {
      // Handle exact abbreviation match or abbreviation at word boundary
      const abbrRegex = new RegExp(`(^|\\s)${abbr}($|\\s)`, 'i');
      if (abbrRegex.test(inputLower) || inputLower === abbr.replace(/[\/&]/g, '')) {
        // Direct abbreviation match - strongly suggest the expansions
        const expandedMatches = expansions.flatMap(expansion => 
          jobTitles.filter(job => 
            job.label.toLowerCase().includes(expansion.toLowerCase())
          )
        );

        if (expandedMatches.length > 0) {
          return {
            suggestion: "",
            job: null,
            didYouMean: expandedMatches[0]
          };
        }
      }
    }

    // Popularity weights for common job titles
    const popularityWeights: {[key: string]: number} = {
      "software engineer": 1.3,
      "product manager": 1.3,
      "data scientist": 1.2,
      "project manager": 1.2,
      "frontend developer": 1.15,
      "backend developer": 1.15,
      "full stack developer": 1.15,
      "ux designer": 1.1,
      "data analyst": 1.1,
      "financial analyst": 1.1,
      "marketing manager": 1.1,
      "sales representative": 1.1,
      "account manager": 1.05,
      "customer success manager": 1.05
    };

    // Calculate similarity scores using multiple matching strategies
    const matches = jobTitles.map(job => {
      const jobLower = job.label.toLowerCase();
      let score = 0;
      let matchType = "";
      
      // Parse job for potential seniority extraction
      let jobCoreRole = jobLower;
      for (const { pattern, reverse } of seniorityPatterns) {
        const match = jobLower.match(pattern);
        if (match) {
          jobCoreRole = reverse ? match[1] : match[2];
          break;
        }
      }
      
      // Exact core role match (disregarding seniority)
      if (jobCoreRole === coreRole) {
        score = 0.95;
        matchType = "core-exact";
      }
      
      // Apply popularity weights to boost common job titles
      Object.keys(popularityWeights).forEach(popularTitle => {
        if (jobLower.includes(popularTitle)) {
          score *= popularityWeights[popularTitle];
        }
      });
      
      // N-gram matching (character sequences of length 2 and 3)
      if (score < 0.7) {
        const getNgrams = (text: string, n: number) => {
          const ngrams = [];
          for (let i = 0; i <= text.length - n; i++) {
            ngrams.push(text.substring(i, i + n));
          }
          return ngrams;
        };
        
        const input2grams = getNgrams(inputLower, 2);
        const job2grams = getNgrams(jobLower, 2);
        const input3grams = getNgrams(inputLower, 3);
        const job3grams = getNgrams(jobLower, 3);
        
        // Count matching n-grams
        const matching2grams = input2grams.filter(ng => job2grams.includes(ng)).length;
        const matching3grams = input3grams.filter(ng => job3grams.includes(ng)).length;
        
        // Calculate Jaccard similarity coefficients
        const similarity2gram = input2grams.length ? matching2grams / (input2grams.length + job2grams.length - matching2grams) : 0;
        const similarity3gram = input3grams.length ? matching3grams / (input3grams.length + job3grams.length - matching3grams) : 0;
        
        // Combined n-gram score
        const ngramScore = similarity2gram * 0.4 + similarity3gram * 0.6;
        score = Math.max(score, ngramScore * 0.9);
        if (ngramScore > 0.6) matchType = "ngram";
      }
      
      // Word boundary recognition
      if (score < 0.8) {
        const inputWords = inputLower.split(/\s+/);
        const jobWords = jobLower.split(/\s+/);
        
        // Check for word-level matches
        let wordMatches = 0;
        let wordPrefixMatches = 0;
        
        for (const inputWord of inputWords) {
          if (inputWord.length < 3) continue; // Skip very short words
          
          for (const jobWord of jobWords) {
            // Exact word match
            if (jobWord === inputWord) {
              wordMatches++;
              break;
            }
            
            // Word prefix match (e.g., "prod" → "product")
            if (jobWord.startsWith(inputWord) && inputWord.length >= 3) {
              wordPrefixMatches++;
              break;
            }
          }
        }
        
        // Calculate word-level matching score
        const exactWordRatio = inputWords.length ? wordMatches / inputWords.length : 0;
        const prefixWordRatio = inputWords.length ? wordPrefixMatches / inputWords.length : 0;
        
        const wordBoundaryScore = exactWordRatio * 0.8 + prefixWordRatio * 0.6;
        score = Math.max(score, wordBoundaryScore);
        if (wordBoundaryScore > 0.6) matchType = "word-boundary";
      }
      
      // Prefix matching with strong emphasis on popular job titles
      if (jobLower.startsWith(inputLower) && inputLower.length >= 3) {
        const prefixScore = 0.7 + (inputLower.length / jobLower.length) * 0.3;
        
        // Boost prefix matches for popular titles
        let popularityBoost = 1.0;
        Object.keys(popularityWeights).forEach(popularTitle => {
          if (jobLower.startsWith(popularTitle.substring(0, Math.min(inputLower.length, popularTitle.length)))) {
            popularityBoost = popularityWeights[popularTitle];
          }
        });
        
        score = Math.max(score, prefixScore * popularityBoost);
        matchType = "prefix";
      }
      
      // For "Did you mean" functionality, we'll offer the complete job title
      return {
        job,
        score,
        matchType,
        suggestion: jobLower.startsWith(inputLower) ? job.label.slice(input.length) : job.label
      };
    })
    .filter(match => match.score > 0.6) // Threshold for quality matches
    .sort((a, b) => b.score - a.score);
    
    if (matches.length === 0) {
      return { suggestion: "", job: null, didYouMean: null };
    }
    
    const bestMatch = matches[0];
    
    // For prefix matches, suggest the completion part
    if (bestMatch.matchType === "prefix") {
      return { 
        suggestion: bestMatch.suggestion, 
        job: bestMatch.job,
        didYouMean: null
      };
    }
    
    // For other match types, suggest as "Did you mean"
    return { 
      suggestion: "",
      job: null,
      didYouMean: bestMatch.job
    };
  };

  // Handle input change and provide suggestions
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    
    // Find best match and set suggestion
    const { suggestion, job, didYouMean: suggestedJob } = findBestMatch(inputValue);
    setSuggestedText(suggestion);
    setMatchingJob(job);
    setDidYouMean(suggestedJob);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Tab or right arrow completes the suggestion
    if ((e.key === 'Tab' || e.key === 'ArrowRight') && suggestedText) {
      e.preventDefault();
      if (matchingJob) {
        onChange(value + suggestedText);
        setSuggestedText("");
        setMatchingJob(null);
      }
    }
    // Tab or Enter for "Did you mean" selection
    else if ((e.key === 'Tab' || e.key === 'Enter') && didYouMean) {
      e.preventDefault();
      onChange(didYouMean.label);
      setDidYouMean(null);
    }
  };

  const handleSuggestionClick = () => {
    if (didYouMean) {
      // Detect if we had extracted seniority from original input
      const seniorityPatterns = [
        { pattern: /^(senior|sr\.?|lead)\s+(.+)$/i, level: "Senior" },
        { pattern: /^(junior|jr\.?)\s+(.+)$/i, level: "Junior" },
        { pattern: /^(principal|staff)\s+(.+)$/i, level: "Principal" },
        { pattern: /^(associate|asst\.?)\s+(.+)$/i, level: "Associate" },
        { pattern: /^(mid|intermediate|mid-level)\s+(.+)$/i, level: "Mid-level" },
        { pattern: /^(chief|head|director\s+of)\s+(.+)$/i, level: "Executive" },
        { pattern: /(.+)\s+(intern|internship)$/i, level: "Intern", reverse: true },
      ];
      
      // Extract seniority from original input if possible
      let originalSeniority = "";
      let originalCore = value.toLowerCase();
      
      for (const { pattern, level, reverse } of seniorityPatterns) {
        const match = value.toLowerCase().match(pattern);
        if (match) {
          originalSeniority = level;
          originalCore = (reverse ? match[1] : match[2]).toLowerCase();
          break;
        }
      }
      
      // If we originally had seniority and we're now suggesting a core role
      // without seniority, preserve our original seniority
      if (originalSeniority && !didYouMean.label.toLowerCase().match(/^(senior|sr\.?|junior|jr\.?|principal|staff|associate|asst\.?|mid|intermediate|mid-level|chief|head|director\s+of)/i)) {
        // Map seniority level back to actual text prefix
        const seniorityPrefix = {
          "Senior": "Senior ",
          "Junior": "Junior ",
          "Principal": "Principal ",
          "Associate": "Associate ",
          "Mid-level": "Mid-level ",
          "Executive": "Chief ",
        }[originalSeniority] || "";
        
        onChange(seniorityPrefix + didYouMean.label);
      } else {
        onChange(didYouMean.label);
      }
      
      setDidYouMean(null);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="smart-predictive-input">
      {label && <label>{label}</label>}
      <div className="predictive-input-container">
        <div className="ghost-input-wrapper">
          <input
            type="text"
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="predictive-input"
          />
          <div className="ghost-text" aria-hidden="true">
            <span className="typed-text">{value}</span>
            <span className="suggested-text">
              {suggestedText}
            </span>
          </div>
        </div>
        
        {didYouMean && (
          <div className="did-you-mean">
            Did you mean: <button onClick={handleSuggestionClick}>{didYouMean.label}</button>
            <span className="hint">(press Tab)</span>
          </div>
        )}
      </div>
      {suggestedText && (
        <div className="input-hint">
          Press <kbd>Tab</kbd> or <kbd>→</kbd> to complete
        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearUser } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [userData, setUserData] = useState<{ firstName: string; lastName: string; email: string; desiredJobRole?: string; experienceLevel?: string } | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const [dropdownAnimation, setDropdownAnimation] = useState<'show' | 'hide' | ''>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Job Matching Modal States
  const [showJobMatchingModal, setShowJobMatchingModal] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [desiredRole, setDesiredRole] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('Entry Level');
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        handleDropdownClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      const parsedUser = JSON.parse(user);
      setUserData({
        firstName: parsedUser.firstName || '',
        lastName: parsedUser.lastName || '',
        email: parsedUser.email || '',
        desiredJobRole: parsedUser.desiredJobRole || '',
        experienceLevel: parsedUser.experienceLevel || 'Entry Level'
      });
      
      // Set the initial values from user data
      setDesiredRole(parsedUser.desiredJobRole || '');
      setExperienceLevel(parsedUser.experienceLevel || 'Entry Level');
      setSelectedJobTypes(parsedUser.preferred_job_types || []);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add a reference to track if data needs to be refreshed
  useEffect(() => {
    // Listen for custom event that AccountInfo will dispatch when user data changes
    const handleUserDataUpdate = () => {
      // Refresh user data from localStorage
      const user = localStorage.getItem('user');
      if (user) {
        const parsedUser = JSON.parse(user);
        setUserData({
          firstName: parsedUser.firstName || '',
          lastName: parsedUser.lastName || '',
          email: parsedUser.email || '',
          desiredJobRole: parsedUser.desiredJobRole || '',
          experienceLevel: parsedUser.experienceLevel || 'Entry Level'
        });
        
        // Also update the form values
        setDesiredRole(parsedUser.desiredJobRole || '');
        setExperienceLevel(parsedUser.experienceLevel || 'Entry Level');
        setSelectedJobTypes(parsedUser.preferred_job_types || []);
      }
    };

    // Listen for the custom event
    window.addEventListener('userProfileUpdated', handleUserDataUpdate);
    
    return () => {
      window.removeEventListener('userProfileUpdated', handleUserDataUpdate);
    };
  }, []);

  const handleLogout = () => {
    // Local clean-up so UI updates instantly
    clearUser();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Let the dedicated Logout route finish any remaining tasks
    navigate('/logout', { replace: true });
  };

  const toggleSidebar = () => {
    if (isExpanded) {
      setIsHovered(false);
      setShowProfileDropdown(false);
    }
    setIsExpanded(!isExpanded);
  };

  const handleDropdownClose = () => {
    setDropdownAnimation('hide');
    setTimeout(() => {
      setShowProfileDropdown(false);
      setDropdownAnimation('');
    }, 200);
  };

  const toggleProfileDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showProfileDropdown) {
      handleDropdownClose();
    } else {
      setShowProfileDropdown(true);
      setDropdownAnimation('show');
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const getInitials = () => {
    if (!userData) return '';
    return `${userData.firstName.charAt(0)}${userData.lastName.charAt(0)}`.toUpperCase();
  };

  const generatePastelColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = hash % 360;
    // Same hue as AccountInfo but more opaque for dark sidebar background
    return `hsla(${h}, 50%, 85%, 0.9)`;
  };

  const handleOverlayClick = () => {
    if (isMobile) {
      setIsExpanded(false);
      setShowProfileDropdown(false);
    }
  };

  const handleOpenJobMatchingModal = () => {
    // Close the profile dropdown first
    setShowProfileDropdown(false);
    setDropdownAnimation('hide');
    
    // Always collapse sidebar to icon-only state when modal opens
    if (isExpanded) {
      setIsExpanded(false);
    }
    if (isHovered) {
      setIsHovered(false);
    }
    
    // Always fetch the most recent user data directly from localStorage
    // This ensures we have the latest data even if it was changed in AccountInfo
    const user = localStorage.getItem('user');
    if (user) {
      const parsedUser = JSON.parse(user);
      
      // Update the userData state
      setUserData(prevData => ({
        ...prevData!,
        desiredJobRole: parsedUser.desiredJobRole || '',
        experienceLevel: parsedUser.experienceLevel || 'Entry Level'
      }));
      
      // Update the form values
      setDesiredRole(parsedUser.desiredJobRole || '');
      setExperienceLevel(parsedUser.experienceLevel || 'Entry Level');
      setSelectedJobTypes(parsedUser.preferred_job_types || []);
    }
    
    // Show the modal
    setShowJobMatchingModal(true);
  };

  const handleCloseJobMatchingModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowJobMatchingModal(false);
      setIsModalClosing(false);
      
      // Remove any modal-related body classes
      document.body.classList.remove('modal-open');
    }, 200);
  };

  const handleJobTypeSelect = (jobType: string) => {
    setSelectedJobTypes(prev => 
      prev.includes(jobType)
        ? prev.filter(t => t !== jobType)
        : [...prev, jobType]
    );
  };

  const handleSaveJobPreferences = async () => {
    try {
      setIsSaving(true);
      
      // Extract seniority if present in the job title
      const seniorityPatterns = [
        { pattern: /^(senior|sr\.?|lead)\s+(.+)$/i, level: "Senior" },
        { pattern: /^(junior|jr\.?)\s+(.+)$/i, level: "Junior" },
        { pattern: /^(principal|staff)\s+(.+)$/i, level: "Principal" },
        { pattern: /^(associate|asst\.?)\s+(.+)$/i, level: "Associate" },
        { pattern: /^(mid|intermediate|mid-level)\s+(.+)$/i, level: "Mid-level" },
        { pattern: /^(chief|head|director\s+of)\s+(.+)$/i, level: "Executive" },
        { pattern: /(.+)\s+(intern|internship)$/i, level: "Intern", reverse: true },
      ];
      
      // Extract seniority from job title if present and not explicitly set
      let detectedExperienceLevel = experienceLevel;
      const jobTitleLower = desiredRole.toLowerCase();
      
      for (const { pattern, level, reverse } of seniorityPatterns) {
        const match = jobTitleLower.match(pattern);
        if (match) {
          // Map seniority level to experience level if not explicitly set
          if (experienceLevel === "Any" || !experienceLevel) {
            switch(level) {
              case "Intern":
                detectedExperienceLevel = "Entry Level (0-2 years)";
                break;
              case "Junior":
                detectedExperienceLevel = "Entry Level (0-2 years)";
                break;
              case "Associate":
                detectedExperienceLevel = "Early Career (2-5 years)";
                break;
              case "Mid-level":
                detectedExperienceLevel = "Mid-Career (5-10 years)";
                break;
              case "Senior":
                detectedExperienceLevel = "Experienced (8-15 years)";
                break;
              case "Principal":
              case "Executive":
                detectedExperienceLevel = "Senior/Executive (15+ years)";
                break;
              default:
                break;
            }
          }
          break;
        }
      }
      
      // Get user from localStorage for the user ID
      const user = localStorage.getItem('user');
      if (!user) {
        throw new Error('User not found in localStorage');
      }
      
      const parsedUser = JSON.parse(user);
      const userId = parsedUser._id;
      
      // Prepare payload with enhanced job matching info
      const jobPreferences = {
        desiredJobRole: desiredRole,
        experienceLevel: detectedExperienceLevel,
        preferred_job_types: selectedJobTypes, // Add job types
        jobLocation: '', // Using empty string as default
        remote: true,    // Default to remote
        maxDistance: 0,  // Default distance
      };
      
      // Make API call to update user preferences using the existing endpoint
      const response = await axiosClient.patch(`/users/${userId}/job-preferences`, jobPreferences);
      
      // Update local user data
      if (response.data) {
        const updatedUser = {
          ...parsedUser,
          desiredJobRole: response.data.desiredJobRole,
          experienceLevel: response.data.experienceLevel,
          desiredJobFamily: response.data.desiredJobFamily,
          desiredJobSubfamily: response.data.desiredJobSubfamily,
          preferred_job_types: response.data.preferred_job_types || selectedJobTypes
        };
        
        // Update local user data - needed for profile display
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUserData({
          ...userData!,
          desiredJobRole: response.data.desiredJobRole,
          experienceLevel: response.data.experienceLevel
        });
        
        // Close the modal immediately
        setShowJobMatchingModal(false);
        
        // Dispatch events to notify components of the update
        try {
          // Create a CustomEvent with role information
          const refreshEvent = new CustomEvent('jobMatchingPreferencesChanged', {
            detail: { 
              timestamp: Date.now(),
              forceRefresh: true,
              newRole: response.data.desiredJobRole,
              newExperience: response.data.experienceLevel
            }
          });
          
          // Dispatch events to update the UI
          window.dispatchEvent(refreshEvent);
          window.dispatchEvent(new Event('userProfileUpdated'));
        } catch (error) {
          console.error('Error dispatching events:', error);
        }
      }
    } catch (error) {
      console.error("Error saving job preferences:", error);
      // Just close the modal on error
      setShowJobMatchingModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isMobile && (
        <button className="mobile-menu-button" onClick={toggleSidebar}>
          <FiMenu />
        </button>
      )}

      {isMobile && isExpanded && (
        <div className="sidebar-overlay visible" onClick={handleOverlayClick} />
      )}

      <div
        className={`sidebar ${isExpanded ? 'expanded' : ''} ${isHovered && !isExpanded ? 'hovered' : ''}`}
        onMouseEnter={() => !isMobile && !isExpanded && setIsHovered(true)}
        onMouseLeave={() => {
          if (!isMobile && !isExpanded) {
            setIsHovered(false);
            handleDropdownClose();
          }
        }}
      >
        <div className="sidebar-top">
          <div className="sidebar-toggle" onClick={toggleSidebar}>
            <FiMenu />
          </div>
          <div className={`sidebar-icon ${isActive('/dashboard') ? 'active' : ''}`} onClick={() => navigate('/dashboard')}>
            <img src={DashboardIcon} alt="Dashboard" className="custom-icon" />
            <span className="icon-text">Dashboard</span>
          </div>
          <div className={`sidebar-icon ${isActive('/job-search') ? 'active' : ''}`} onClick={() => navigate('/job-search')}>
            <img src={JobSearchIcon} alt="Job Search" className="custom-icon" />
            <span className="icon-text">Job Search</span>
          </div>
          <div className={`sidebar-icon ${isActive('/application-tracker') ? 'active' : ''}`} onClick={() => navigate('/application-tracker')}>
            <img src={ApplicationTrackerIcon} alt="Application Tracker" className="custom-icon" />
            <span className="icon-text">Application Tracker</span>
          </div>
          <div className={`sidebar-icon ${isActive('/bring-your-job') ? 'active' : ''}`} onClick={() => navigate('/bring-your-job')}>
            <img src={BYOJIcon} alt="Bring Your Own Job" className="custom-icon" />
            <span className="icon-text">Bring Your Own Job</span>
          </div>
        </div>
        <div className="sidebar-bottom">
          <div className="profile-section" ref={profileRef} onClick={toggleProfileDropdown}>
            <div className="profile-trigger">
              <div className="avatar-wrapper">
                <div 
                  className="avatar"
                  style={{
                    backgroundColor: userData ? generatePastelColor(userData.firstName + userData.lastName) : '#f0f0f0'
                  }}
                >
                  {getInitials()}
                </div>
              </div>
              {(isExpanded || isHovered) && (
                <>
                  <div className="user-info">
                    <span className="user-name">{userData?.firstName} {userData?.lastName}</span>
                    <span className="user-email">{userData?.email}</span>
                  </div>
                  <FiChevronUp className={`trigger-icon ${showProfileDropdown ? 'rotated' : ''}`} />
                </>
              )}
            </div>
            {showProfileDropdown && (
              <div className={`profile-dropdown ${dropdownAnimation}`}>
                <div className="dropdown-section">
                  <div className="dropdown-item" onClick={() => navigate('/profile')}>
                    <FiUser />
                    <span>Profile</span>
                  </div>
                  <div className="dropdown-item" onClick={handleOpenJobMatchingModal}>
                    <Settings className="lucide-icon" />
                    <span>Job Matching</span>
                  </div>
                  <div className="dropdown-item" onClick={() => navigate('/subscription')}>
                    <Wallet className="lucide-icon" />
                    <span>Manage Plan</span>
                  </div>
                </div>
                <div className="dropdown-section">
                  <div className="dropdown-item" onClick={handleLogout}>
                    <FiLogOut />
                    <span>Log out</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Job Matching Preferences Modal */}
      {showJobMatchingModal && (
        <div
          className={`job-matching-modal-overlay modal-open ${isModalClosing ? "closing" : ""}`}
          onClick={handleCloseJobMatchingModal}
        >
          <div className="job-matching-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-matching-modal-content">
              <h2>Job Matching Preferences</h2>
              <p>Set your job matching preferences to get personalized recommendations.</p>

              <div className="job-matching-modal-field">
                <SmartPredictiveInput
                  value={desiredRole}
                  onChange={setDesiredRole}
                  jobTitles={jobTitles}
                  placeholder="e.g. Software Engineer, Product Manager"
                  label="Desired Role"
                />
              </div>

              {/* Option bubbles for experience level */}
              <div className="job-matching-modal-field">
                <label>Experience Level</label>
                <div className="option-bubbles-container">
                  {["Entry Level", "Mid Career", "Senior Level"].map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`option-bubble ${experienceLevel === option ? 'selected' : ''}`}
                      onClick={() => setExperienceLevel(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Option bubbles for job types */}
              <div className="job-matching-modal-field">
                <label>Job Types</label>
                <div className="option-bubbles-container">
                  {["Full-time", "Part-time", "Internship", "Contract"].map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`option-bubble ${selectedJobTypes.includes(option.toLowerCase()) ? 'selected' : ''}`}
                      onClick={() => handleJobTypeSelect(option.toLowerCase())}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="job-matching-modal-actions">
                <button 
                  className="keep-button" 
                  onClick={handleCloseJobMatchingModal} 
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button 
                  className="discard-button" 
                  onClick={handleSaveJobPreferences}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
              
              {/* Success and error messages */}
              {successMessage && (
                <div className="success-message">
                  {successMessage}
                </div>
              )}
              
              {errorMessage && (
                <div className="error-message">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;